import React, { useState, useEffect, useContext, useMemo } from 'react'
import axios from 'axios'
import { useTable, usePagination, useSortBy, useGlobalFilter } from 'react-table'
import { useLocation, useParams } from 'react-router-dom'
import { baseApiURL } from 'src/utils/utils'
import { DispatchContext, StateContext } from '../../AppContext'
import Header from 'src/components/header/Header'
import SearchField from 'src/components/searchField/SearchField'
import Button from 'src/components/button/Button'
import format from 'date-fns/format'
import differenceInCalendarDays from 'date-fns/differenceInCalendarDays'
import { IState, ParamTypes } from 'src/Interface'
import { toast } from 'react-toastify'
import Footer from 'src/components/footer/Footer'
import Table from 'src/components/table/Table'
import SubHeader from 'src/components/subHeader/SubHeader'
import useCancellablePromise from 'src/utils/useCancellablePromise'

const Users: React.FC = () => {
  const { companyId, branchId } = useParams<ParamTypes>()
  const [loading, setLoading] = useState(true)
  const dispatch: React.Dispatch<any> = useContext(DispatchContext)
  const state: IState = useContext(StateContext)
  const location: any = useLocation()
  const { cancellablePromise } = useCancellablePromise()
  const [hideArchieved, setHideArchived] = useState(JSON.parse(window.localStorage.getItem('hideUserArchieved')) || false)

  const toggleArchieved = (hideArchieved) => {
    window.localStorage.setItem('hideUserArchieved', hideArchieved)
    setHideArchived(hideArchieved)
    dispatch({ type: 'SHOW_ASIDE', show: false, asideType: null })
  }

  const data = useMemo(() => state.users, [state.users])
  const columns = useMemo(() => {
    const roles = {
      DanTaxiAdmin: 'Dantaxi Admin',
      CompanyAdmin: 'Admin',
      BranchAdmin: 'Manager',
      BranchEmployee: 'Bruger'
    }
    return [
      {
        Header: 'Navn',
        accessor: properties => `${properties.firstName} ${properties.lastName}`
      },
      {
        Header: 'E-mail',
        accessor: 'email'
      },
      {
        Header: 'Rolle',
        accessor: 'role',
        Cell: property => {
          return roles[property.value]
        }
      },
      {
        Header: 'Bookinger',
        accessor: 'ordersCount'
      },
      {
        Header: 'Sidste 30 dage	',
        accessor: 'latestOrdersCount'
      },
      {
        Header: 'Sidste aktivitet',
        accessor: 'lastActivityTimestamp',
        Cell: (item) => {
          return (
            item.value ?
              <p className='tw-flex'>{
                differenceInCalendarDays(new Date(), new Date(item.value)) === 0 ?
                  <>
                    <span className='tw-mr-3'>I dag,</span>
                    <span>{format(new Date(item.value), 'HH:mm')}</span>
                  </> :
                  <>
                    <span className='tw-mr-3'>{differenceInCalendarDays(new Date(), new Date(item.value))}</span>
                    <span>dage</span>
                  </>
              }</p>
              : <p>Ingen aktivitet endnu</p>
          )
        }
      },
      {
        Header: 'Aktiv',
        accessor: 'status',
        Cell: (cell) => cell.value === 'ACTIVE' ? <span className='status status--green'>Aktiv</span> : cell.value === 'INVITED' ? <span className='status status--blue'>Inviteret</span> : <span className='status status--red'>Deaktiveret</span>
      }
    ]
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    canNextPage,
    canPreviousPage,
    gotoPage,
    pageOptions,
    previousPage,
    state: pageState,
    nextPage,
    page,
    setGlobalFilter,
    prepareRow,
  } = useTable({
    columns,
    data,
    autoResetPage: false
  }, useGlobalFilter, useSortBy, usePagination)

  useEffect(() => {
    if (state.user.accessToken) {
      cancellablePromise(axios(`${baseApiURL()}/users${branchId ? `?branchId=${branchId}&companyId=${companyId}` : `?companyId=${companyId}`}`, {
        headers: {
          Authorization:
            `Bearer ${state.user.accessToken}`
        }
      }).then((result) => {
        setLoading(false)
        dispatch({ type: 'SET_USERS', users: hideArchieved ? result.data.filter(user => user.status !== 'INACTIVE') : result.data })
      }).catch((err) => {
        toast(err.response.data.message)
      }))
    }
  }, [dispatch, state.user.accessToken, hideArchieved, branchId, companyId, cancellablePromise])

  return (
    <>
      <Header />
      <SubHeader loading={loading} backPath={location.state.backPath ? location.state.backPath : '/companies'} title={
        <>
          <svg className='arrow-icon' xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span className='color-gray tw-font-normal'>{location.state.from}</span> <span className='tw-mx-3 color-gray tw-font-normal'>/</span> <span className='text-black text-bold'>Medarbejdere</span>
        </>
      }>
        <Button type='secondary' label='Indstillinger' iconLeft={<svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>} modifiers='tw-mr-5' handleClick={() => dispatch({ type: 'SHOW_ASIDE', show: true, checked: hideArchieved, asideType: 'Settings', size: 'small', ToggleSwitchLabel: 'Skjul deaktiverede medarbejdere', handleClick: toggleArchieved })} />
        <SearchField name='search' label='Søg' placeholder='Søg' handleInputChange={(value) => setGlobalFilter(value)} />
        <Button modifiers='tw-ml-5' type='primary' label='Opret medarbejder' handleClick={() => dispatch({ showModal: true, type: 'SHOW_MODAL', modalType: 'addUser', companyId, branchId })} />
      </SubHeader>
      <main className='container'>
        <Table
          getTableBodyProps={getTableBodyProps}
          getTableProps={getTableProps}
          prepareRow={prepareRow}
          page={page}
          loading={loading}
          headerGroups={headerGroups}
          type='users'
          handleClick={(userId => dispatch({ type: 'SHOW_ASIDE', hideArchieved, asideType: 'UserDetails', show: true, companyId, userId, branchId }))}
          state={{ background: location }}
        />
      </main>
      <Footer
        pageState={pageState}
        canNextPage={canNextPage}
        nextPage={nextPage}
        loading={loading}
        canPreviousPage={canPreviousPage}
        page={page}
        paginationLabel='medarbejdere'
        gotoPage={gotoPage}
        previousPage={previousPage}
        pageOptions={pageOptions}
        itemsLength={state.users.length}
      />
    </>
  )
}

export default Users
